import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { useAuthStore } from './store/authStore';
import { useWebSocket } from './hooks/useWebSocket';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import LiveMapPage from './pages/LiveMapPage';
import SOSCenterPage from './pages/SOSCenterPage';
import PlatoonsPage from './pages/PlatoonsPage';
import GeofencesPage from './pages/GeofencesPage';
import OperationsPage from './pages/OperationsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AlertsPage from './pages/AlertsPage';
import UnitStatisticsPage from './pages/UnitStatisticsPage';
import SettingsPage from './pages/SettingsPage';
import LocationStudioPage from './pages/LocationStudioPage';
import TargetsPage from './pages/TargetsPage';
import CommandCenterPage from './pages/CommandCenterPage';
import WarDiaryPage from './pages/WarDiaryPage';
import OpsWallPage from './pages/OpsWallPage';
import CommsPage from './pages/CommsPage';
import OpPlansPage from './pages/OpPlansPage';
import AlertRulesPage from './pages/AlertRulesPage';
import AdminPage from './pages/AdminPage';

// Simulation Center is lazy-loaded so the entire feature (engine + heavy
// scenario data) stays out of the operational app's initial bundle.
const SimulationPage = lazy(() => import('./pages/SimulationPage'));

function SimulationFallback() {
  return (
    <div className="flex-1 min-h-[420px] flex items-center justify-center bg-command-bg">
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 border-4 border-command-accent border-t-transparent rounded-full animate-spin" />
        <p className="text-command-accent font-display tracking-wider text-sm">LOADING SIMULATION…</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-command-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-command-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-command-accent font-display tracking-wider">INITIALIZING...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { checkAuth, isAuthenticated } = useAuthStore();
  
  // Initialize WebSocket connection when authenticated
  useWebSocket();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
        } />
        
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="map" element={<LiveMapPage />} />
          <Route path="command-center" element={<CommandCenterPage />} />
          <Route path="location-studio" element={<LocationStudioPage />} />
          <Route path="targets" element={<TargetsPage />} />
          <Route path="war-diary" element={<WarDiaryPage />} />
          <Route path="comms" element={<CommsPage />} />
          <Route path="op-plans" element={<OpPlansPage />} />
          <Route path="ops-wall" element={<OpsWallPage />} />
          <Route
            path="simulation"
            element={
              <Suspense fallback={<SimulationFallback />}>
                <SimulationPage />
              </Suspense>
            }
          />
          <Route path="unit-statistics" element={<UnitStatisticsPage />} />
          <Route path="sos" element={<SOSCenterPage />} />
          <Route path="platoons" element={<PlatoonsPage />} />
          <Route path="operations" element={<OperationsPage />} />
          <Route path="geofences" element={<GeofencesPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="alert-rules" element={<AlertRulesPage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

